import React, { FC } from 'react';
import { useGlobalSettings } from '../hooks/useGlobalSettings';
import { colors } from '../styles/tokens/colors';
import { Kfh_ContentSeoType } from '../types/graphqlTypes';

interface SeoProps extends Record<string, any> {
  title?: string;
  meta?: [];
  image?: string;
  data?: Kfh_ContentSeoType;
  schema?: any[];
  canonical?: string;
}

export const SEO: FC<SeoProps> = ({ data, meta = [], title, schema = [], image, canonical }) => {
  const { socialSettings, siteSettings, siteMeta } = useGlobalSettings();

  const inLanguage = 'en-GB';

  const sameAs: string[] = [];

  if (socialSettings.twitterScreenName) {
    sameAs.push(`https://twitter.com/${socialSettings.twitterScreenName}`);
  }
  if (socialSettings.linkedInUsername) {
    sameAs.push(`https://www.linkedin.com/company/${socialSettings.linkedInUsername}`);
  }

  const companySchema = {
    '@context': 'http://schema.org',
    '@type': 'Organization',
    name: siteSettings.companyName,
    alternateName: siteMeta.shortName,
    url: siteMeta.siteUrl,
    sameAs,
  };

  const fullSchema = [companySchema, ...schema];

  const verification: any[] = [
    {
      name: 'google-site-verification',
      content: siteMeta?.googleVerification,
    },
    {
      name: 'p:domain_verify',
      content: siteMeta?.pinterestVerification,
    },
  ];

  const metaTitle = title || data?.title || 'KFH';
  const metaDescription = data?.metaDescription ? data.metaDescription : '';
  const robotsIndex = data?.noIndex ? 'noindex' : 'index';
  const robotsFollow = data?.noFollow ? 'nofollow' : 'follow';
  const brandColor = colors?.brand.default || null;

  const metaItems = [
    {
      name: `robots`,
      content: `max-snippet:-1, max-image-preview:large, max-video-preview:-1, ${robotsIndex}, ${robotsFollow}`,
    },
    {
      name: `description`,
      content: metaDescription.replace(/<br\s*[\/]?>/gi, '\n'),
    },
    {
      name: `author`,
      content: data?.metaAuthor || null,
    },
    {
      name: `copyright`,
      content: data?.metaCopyright || null,
    },
    {
      name: `theme-color`,
      content: brandColor,
    },
    {
      name: `msapplication-TileColor`,
      content: brandColor,
    },
    {
      name: `msapplication-navbutton-color`,
      content: brandColor,
    },
    {
      name: `apple-mobile-web-app-status-bar-style`,
      content: brandColor,
    },
    {
      property: `og:site_name`,
      content: siteSettings?.companyName,
    },
    {
      property: `og:title`,
      content: data?.h1 || metaTitle,
    },
    {
      property: `og:description`,
      content: metaDescription,
    },
    {
      property: `og:locale`,
      content: inLanguage.replace('-', '_'),
    },
    {
      property: `og:type`,
      content: `website`,
    },
    {
      property: `og:image`,
      content: image,
    },
    {
      name: `twitter:card`,
      content: `summary`,
    },
    {
      name: `twitter:creator`,
      content: socialSettings?.twitterScreenName,
    },
    {
      name: `twitter:title`,
      content: data?.h1 || metaTitle,
    },
    {
      name: `twitter:description`,
      content: metaDescription,
    },
    {
      name: 'twitter:image',
      content: image,
    },
  ]
    .filter((m) => !!m.content)
    .concat(meta, verification);

  return (
    <>
      <title id="kfh-seo-title">{metaTitle}</title>
      {metaItems.map((metaItem, index) => (
        <meta
          name={metaItem?.name}
          id={`kfh-seo-${metaItem?.name}`}
          content={metaItem?.content as string}
          property={metaItem?.property as string}
          key={index}
        />
      ))}
      {canonical && <link rel="canonical" href={canonical} id="kfh-canonical" />}
      {/* <script type="text/javascript">var dataLayer = dataLayer || [];</script> */}
      {fullSchema && (
        <script type="application/ld+json" id="kfh-seo-schema">
          {JSON.stringify(fullSchema, undefined, process.env.NODE_ENV === 'development' ? 2 : 0)}
        </script>
      )}
    </>
  );
};
